import React, { useState, useMemo } from "react";
import HistorialSST from "./components/HistorialSST";
import MedidasDisciplinarias from "./components/MedidasDisciplinarias"; 
import Contratos from "./components/Contratos"; 
import GestorDocumentalTH from "./components/GestorDocumentalTH"; 
import HistorialPermisos from './permisos/HistorialPermisos';
import DirectorioSedes from "./components/DirectorioSedes";
import ActasCompromisoView from "./components/ActasCompromisoView";

const DICCIONARIO_VISUAL = {
  "Medidas Disciplinarias": { icono: "⚖️", desc: "Registro de novedades y llamados de atención.", categoria: "RRHH" },
  "Actas de Compromiso": { icono: "🤝", desc: "Planes de mejora continua y seguimiento .", categoria: "RRHH" },
  "Contratos": { icono: "📄", desc: "Gestión documental y archivos PDF.", categoria: "RRHH" },
  
  "Formatos y Trámites": { icono: "🗂️", desc: "Historial de solicitudes de permisos y traslados.", categoria: "TRAMITES" }, 
  "Proceso Permisos": { icono: "✅", desc: "Gestión y autorización de ausentismos.", categoria: "TRAMITES" },

  "Directorio": { icono: "📞", desc: "Teléfonos corporativos y contactos por sede.", categoria: "DOCUMENTOS" },
  "Organigrama": { icono: "🏢", desc: "Estructura jerárquica corporativa.", categoria: "DOCUMENTOS" },
  "Reglamento Interno": { icono: "📖", desc: "Normativas y reglas oficiales.", categoria: "DOCUMENTOS" },
  "Políticas T.H.": { icono: "🛡️", desc: "Directrices de Talento Humano.", categoria: "DOCUMENTOS" },
  "Onboarding": { icono: "🚀", desc: "Material de inducción y bienvenida.", categoria: "DOCUMENTOS" },
  "Comunicados": { icono: "📢", desc: "Avisos y boletines oficiales.", categoria: "DOCUMENTOS" },
  "Manuales de Cargo": { icono: "📘", desc: "Roles y responsabilidades de cargos.", categoria: "DOCUMENTOS" },
  "Perfiles de Cargos": { icono: "📝", desc: "Plantillas y documentos institucionales.", categoria: "DOCUMENTOS" },  
  "Rutas": { icono: "🚌", desc: "Gestión y listados de rutas de transporte.", categoria: "PROGRAMAS" },
  "Personal con Discapacidad": { icono: "♿", desc: "Registro y seguimiento de personal.", categoria: "PROGRAMAS" },
  "Maternas": { icono: "🤰", desc: "Seguimiento de licencias y estado de gestación.", categoria: "PROGRAMAS" },
  "Auditoría SST": { icono: "📊", desc: "Historial General de SST y sanidad.", categoria: "SST" }
};

const normalizeKey = (str) => {
  if (!str) return "";
  return String(str)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
    .toLowerCase()
    .replace(/\s+/g, "") 
    .replace(/s$/, "");  
};

const getPermisoAvanzadoFlexible = (permisosDB, nombreBuscado) => {
  if (!permisosDB) return undefined;
  const buscadoNorm = normalizeKey(nombreBuscado);
  
  for (const key in permisosDB) {
    if (normalizeKey(key) === buscadoNorm) {
      return permisosDB[key];
    }
  }
  return undefined;
};

export default function GestionHumana({ usuario, onBackToMenu }) {
  const [subVista, setSubVista] = useState("menu"); 
  const [categoriaDoc, setCategoriaDoc] = useState(''); 
  const [permisosContexto, setPermisosContexto] = useState({ crear: false, editar: false, eliminar: false });

  const permisosABAC = useMemo(() => {
    try {
      if (!usuario || !usuario.permisos_avanzados) return {};
      const parsed = typeof usuario.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : usuario.permisos_avanzados;
      return parsed.micros || parsed || {};
    } catch (e) { return {}; }
  }, [usuario]);

  const esSuperAdmin = String(usuario?.cargo || "").toUpperCase().includes("SUPER");

  const tarjetasPermitidas = useMemo(() => {
    const permitidas = [];
    
    Object.keys(DICCIONARIO_VISUAL).forEach(nombreFormulario => {
      const permiso = getPermisoAvanzadoFlexible(permisosABAC, nombreFormulario);
      
      let puedeVer = esSuperAdmin;
      if (!esSuperAdmin && permiso !== undefined) {
        if (typeof permiso === 'object' && permiso !== null) puedeVer = permiso.ver !== false; 
        else puedeVer = true; 
      }
      
      if (puedeVer) permitidas.push({ id: nombreFormulario, titulo: nombreFormulario, ...DICCIONARIO_VISUAL[nombreFormulario] });
    });
    return permitidas;
  }, [permisosABAC, esSuperAdmin]);

  const rrhhCards = tarjetasPermitidas.filter(t => t && t.categoria === "RRHH");
  const tramitesCards = tarjetasPermitidas.filter(t => t && t.categoria === "TRAMITES");
  const documentosCards = tarjetasPermitidas.filter(t => t && t.categoria === "DOCUMENTOS");
  const programasCards = tarjetasPermitidas.filter(t => t && t.categoria === "PROGRAMAS");
  const sstCards = tarjetasPermitidas.filter(t => t && t.categoria === "SST");
  
  const tienePermisos = tarjetasPermitidas.length > 0;

  const handleCardClick = (idTarjeta) => {
    const permisoExtraido = getPermisoAvanzadoFlexible(permisosABAC, idTarjeta) || {};
    
    const permisosCRUD = {
      crear: esSuperAdmin ? true : (permisoExtraido.crear === true),
      editar: esSuperAdmin ? true : (permisoExtraido.editar === true),
      eliminar: esSuperAdmin ? true : (permisoExtraido.eliminar === true),
    };
    
    setPermisosContexto(permisosCRUD);
    if (idTarjeta === "Auditoría SST") setSubVista("historialSST");
    else if (idTarjeta === "Medidas Disciplinarias") setSubVista("disciplinarios");
    else if (idTarjeta === "Actas de Compromiso") setSubVista("actas_compromiso");
    else if (idTarjeta === "Contratos") setSubVista("contratos");
    else if (idTarjeta === "Formatos y Trámites") setSubVista("historial_tramites");
    else if (idTarjeta === "Directorio") setSubVista("directorio"); 
    else { 
      setCategoriaDoc(idTarjeta); 
      setSubVista("documentos"); 
    }
  };

  if (subVista === "menu") {
    if (!tienePermisos) {
      return (
        <div className="w-full max-w-4xl bg-white/70 backdrop-blur-xl rounded-[32px] shadow-xl border border-white p-12 text-center mx-auto animate-fade-in-up">
          <span className="text-7xl block mb-6 drop-shadow-md">🔒</span>
          <h2 className="text-3xl font-black text-slate-800 mb-3 tracking-tight">Acceso Denegado</h2>
          <p className="text-base text-slate-600 mb-8 font-medium max-w-lg mx-auto">
            El módulo está activo, pero no tienes permisos específicos en ABAC para ver sus formularios.
          </p>
          <button onClick={onBackToMenu} className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg transition-all cursor-pointer btn-glow">Volver al Menú</button>
        </div>
      );
    }

    return (
      <div className="bg-white/40 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl w-full mx-auto border border-white/80 animate-fade-in relative overflow-hidden h-[88vh] overflow-y-auto custom-scrollbar">
        
        <div className="flex items-center gap-5 mb-10 pb-6 border-b border-slate-200/50 animate-fade-in-down">
          <button 
            onClick={onBackToMenu}
            className="w-14 h-14 flex-none bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center rounded-2xl transition-all shadow-sm border border-slate-200 cursor-pointer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
          </button>
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Gestión Humana</h1>
            <p className="text-sm font-bold text-slate-500 mt-1">Administración de personal y control corporativo</p>
          </div>
        </div>

        {rrhhCards.length > 0 && (
          <div className="mb-10">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-widest mb-4 pl-2">Recursos Humanos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rrhhCards.map((t, index) => (
                <div key={t.id} onClick={() => handleCardClick(t.id)} className={`bg-white/60 hover:bg-white/90 p-6 sm:p-8 rounded-[28px] border border-white shadow-lg hover:shadow-xl transition-all text-left group cursor-pointer card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                  <div className="w-14 h-14 bg-indigo-50/80 rounded-2xl flex items-center justify-center text-3xl border border-indigo-100 mb-5 icon-bounce">{t.icono}</div>
                  <h3 className="text-lg font-black text-slate-800 leading-tight mb-1.5">{t.titulo}</h3>
                  <p className="text-xs text-slate-500 font-bold">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tramitesCards.length > 0 && (
          <div className="mb-10">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-widest mb-4 pl-2">Trámites y Solicitudes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tramitesCards.map((t, index) => (
                <div key={t.id} onClick={() => handleCardClick(t.id)} className={`bg-white/60 hover:bg-white/90 p-6 sm:p-8 rounded-[28px] border border-white shadow-lg hover:shadow-xl transition-all text-left group cursor-pointer card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                  <div className="w-14 h-14 bg-blue-50/80 rounded-2xl flex items-center justify-center text-3xl border border-blue-100 mb-5 icon-bounce">{t.icono}</div>
                  <h3 className="text-lg font-black text-slate-800 leading-tight mb-1.5">{t.titulo}</h3>
                  <p className="text-xs text-slate-500 font-bold">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {programasCards.length > 0 && (
          <div className="mb-10">
            <h3 className="text-[11px] font-black text-purple-600 uppercase tracking-widest mb-4 pl-2">Programas Especiales y Rutas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {programasCards.map((t, index) => (
                <div key={t.id} onClick={() => handleCardClick(t.id)} className={`bg-white/60 hover:bg-white/90 p-6 sm:p-8 rounded-[28px] border border-white shadow-md hover:shadow-xl transition-all text-left group cursor-pointer card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                  <div className="w-14 h-14 bg-purple-50/80 rounded-2xl flex items-center justify-center text-3xl border border-purple-100 mb-5 icon-bounce">{t.icono}</div>
                  <h3 className="text-lg font-black text-slate-800 leading-tight mb-1.5">{t.titulo}</h3>
                  <p className="text-xs text-slate-500 font-bold">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {documentosCards.length > 0 && (
          <div className="mb-10">
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-widest mb-4 pl-2">Biblioteca Documental</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {documentosCards.map((t, index) => (
                <div key={t.id} onClick={() => handleCardClick(t.id)} className={`bg-white/60 hover:bg-white/90 p-6 rounded-[28px] border border-white shadow-md hover:shadow-xl transition-all text-left group cursor-pointer card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                  <div className="w-12 h-12 bg-emerald-50/80 rounded-xl flex items-center justify-center text-2xl border border-emerald-100 mb-4 icon-bounce">{t.icono}</div>
                  <h3 className="text-sm font-black text-slate-800">{t.titulo}</h3>
                </div>
              ))}
            </div>
          </div>
        )}

        {sstCards.length > 0 && (
          <div>
            <h3 className="text-[11px] font-black text-blue-600 uppercase tracking-widest mb-4 pl-2">Seguridad y Salud en el Trabajo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {sstCards.map((t, index) => (
                <div key={t.id} onClick={() => handleCardClick(t.id)} className={`bg-white/60 hover:bg-white/90 p-6 rounded-[28px] border border-white shadow-md hover:shadow-xl transition-all text-left flex items-center gap-5 group cursor-pointer card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                  <div className="w-14 h-14 flex-none bg-slate-50/80 rounded-2xl flex items-center justify-center text-3xl border border-slate-200 icon-bounce">{t.icono}</div>
                  <div>
                    <h3 className="text-base font-black text-slate-800 leading-tight mb-1">{t.titulo}</h3>
                    <p className="text-[11px] text-slate-500 font-bold">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    );
  }

  if (subVista === "historialSST") return <HistorialSST usuario={usuario} onBack={() => setSubVista("menu")} />;
  if (subVista === "disciplinarios") return <MedidasDisciplinarias usuario={usuario} onBackToMenu={() => setSubVista("menu")} />;
  if (subVista === "actas_compromiso") {
    return <ActasCompromisoView usuario={usuario} permisosCRUD={permisosContexto} onBack={() => setSubVista("menu")} />;
  }
  if (subVista === "contratos") return <Contratos usuario={usuario} onBackToMenu={() => setSubVista("menu")} />;
  if (subVista === "historial_tramites") {
    const permisosFormatos = getPermisoAvanzadoFlexible(permisosABAC, 'Formatos y Trámites') || { crear: false, editar: false, eliminar: false };
    return <HistorialPermisos usuario={usuario} filtroABAC={permisosFormatos} onBack={() => setSubVista("menu")} />;
  }
  if (subVista === "directorio") return <DirectorioSedes usuario={usuario} onBackToMenu={() => setSubVista("menu")} />;
  
  if (subVista === "documentos") {
    return <GestorDocumentalTH usuario={usuario} categoria={categoriaDoc} permisosCRUD={permisosContexto} onBackToMenu={() => setSubVista("menu")} />;
  }

  return null;
}